/**
 * Shared recency-window helpers for the Watchlist News UI.
 *
 * Split out of NewsCell so NewsPopover can import them without creating a
 * NewsCell ⇄ NewsPopover circular import (NewsCell already imports
 * NewsPopover for its rendered surface).
 *
 * W8 (2026-08-04): the recency window is a per-user preference (1 / 2 / 6 /
 * 24 / 48 hr) plumbed in as `recencyWindowHours`. This file owns:
 *   - the default (24 hr, matches the pre-W8 hardcode);
 *   - the "which articles count as recent" filter;
 *   - the tooltip copy for the recency dot (varies by hours + read/unread).
 */

export const DEFAULT_RECENCY_WINDOW_HOURS = 24;
export const NEW_NEWS_RECENCY_MS = DEFAULT_RECENCY_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * Compute the subset of `articles` whose `published_at` is inside the
 * `windowHours` recency window (strict `<` — an article published exactly
 * `windowHours` ago does NOT count, matching W7's boundary behavior).
 *
 * Defensive against null / bad timestamps — a bogus ISO string is silently
 * dropped rather than crashing rendering.
 */
export function selectRecentArticles(articles, windowHours, nowMs = Date.now()) {
    if (!Array.isArray(articles)) return [];
    const windowMs = Number(windowHours) * 60 * 60 * 1000;
    if (!Number.isFinite(windowMs) || windowMs <= 0) return [];
    const out = [];
    for (const article of articles) {
        const raw = article?.published_at;
        if (!raw) continue;
        const parsed = raw instanceof Date ? raw : new Date(raw);
        const ts = parsed.getTime();
        if (!Number.isFinite(ts)) continue;
        if ((nowMs - ts) < windowMs) {
            out.push(article);
        }
    }
    return out;
}

/**
 * Recency-dot tooltip copy. Varies by `windowHours` and read state so the dot
 * always self-describes what it's showing (Trevor 2026-07-30: read/unread
 * differentiation).
 */
export function formatRecencyDotTooltip(windowHours, { allRead }) {
    const hours = Number(windowHours);
    const safeHours = Number.isFinite(hours) && hours > 0
        ? hours
        : DEFAULT_RECENCY_WINDOW_HOURS;
    const hoursLabel = Number.isInteger(safeHours)
        ? String(safeHours)
        : String(Number(safeHours.toFixed(2)));
    const suffix = safeHours === 1 ? 'hour' : 'hours';
    if (allRead) {
        return `All news in the last ${hoursLabel} ${suffix} read`;
    }
    return `News in the last ${hoursLabel} ${suffix}`;
}


export function newsFetchMessage(item) {
    if (item?.fetch_status === 'pending') return 'Checking for news…';
    if (item?.fetch_status === 'retrying') return 'News temporarily unavailable. We’ll retry automatically.';
    if (item?.fetch_status === 'empty') return 'News checked. No matching headlines found.';
    return 'No recent news.';
}
